// Textes juridiques du site, en français et en anglais.
//
// Ils vivaient jusqu'ici en JSX codé en dur dans App.js, donc uniquement en
// français alors que le site est bilingue : un visiteur anglophone lisait une
// politique de confidentialité qu'il ne comprenait pas.
//
// Règle de tenue de ce fichier : ce qui est écrit ici doit décrire ce que le code
// fait réellement. Chaque fois qu'une donnée nouvelle est collectée, qu'un
// prestataire est ajouté ou qu'une durée de conservation change, la section
// correspondante se met à jour ici — sinon la politique redevient une fiction.
//
// LEGAL_VERSION est la date de la dernière révision de fond. Elle est affichée
// telle quelle : avant, la modale affichait `new Date()`, donc le document se
// prétendait modifié chaque jour, ce qui lui retirait toute valeur de preuve.

export const LEGAL_VERSION = "2026-09-13";

const CONTACT = "contact@roast-my-stats.com";

const legal = {
  fr: {
    updated: (v) => `Dernière mise à jour : ${v}`,
    privacy: [
      {
        title: "1. Données que nous collectons",
        blocks: [
          { type: "p", text: "Selon que tu as un compte ou non, Roast My Stats enregistre :" },
          {
            type: "ul",
            items: [
              "<strong>Compte :</strong> adresse email, prénom (facultatif), mot de passe haché avec bcrypt, date de création, code de parrainage et, le cas échéant, le code de parrainage utilisé à l'inscription.",
              "<strong>Analyses :</strong> pseudos recherchés, scores et badges obtenus, dates. Si tu rends ton profil public, le dernier rapport complet est conservé pour alimenter la page Communauté.",
              "<strong>Visiteurs sans compte :</strong> une empreinte d'un identifiant aléatoire tiré par ton navigateur, et une empreinte de ton adresse IP (toutes deux hachées en SHA-256 avec un sel secret, non réversibles en pratique), pour limiter le nombre d'analyses gratuites par jour. Le décompte se fait sur l'identifiant de navigateur, l'empreinte d'IP ne servant qu'à borner les usages massifs. Ni l'identifiant en clair, ni l'adresse elle-même ne sont conservés.",
              "<strong>Mesure d'audience :</strong> une empreinte de ton adresse IP (hachée en SHA-256, non réversible en pratique) et la date, pour compter les visiteurs uniques par jour. Nous ne la croisons avec aucune autre donnée.",
              "<strong>Avis et signalements :</strong> la note, le commentaire éventuel et une empreinte hachée de l'adresse IP, pour éviter qu'un même visiteur vote en boucle.",
              "<strong>Pass 24h :</strong> ton adresse email, et la trace horodatée de ton clic sur le bouton du Pass — avec l'empreinte de navigateur décrite plus haut, la langue et, si tu as un compte, son identifiant. Le paiement n'étant pas encore ouvert, ces traces servent à mesurer l'intérêt pour l'offre avant de la construire.",
              "<strong>Messages :</strong> le contenu des emails que tu nous envoies, pour pouvoir te répondre.",
              "<strong>Paiement du Pass :</strong> ton identifiant client chez notre prestataire de paiement, si tu achètes un jour un Pass 24h. Nous ne voyons ni ne stockons ton numéro de carte.",
            ],
          },
        ],
      },
      {
        title: "2. À quoi elles servent",
        blocks: [
          {
            type: "ul",
            items: [
              "Créer ton compte, t'y connecter et te permettre de le supprimer.",
              "Envoyer les emails de vérification et de réinitialisation de mot de passe.",
              "T'envoyer, une fois par semaine, un récapitulatif de tes parties analysées, si tu as joué durant la semaine. Chaque email contient un lien de désinscription en un clic, et tu peux couper cet envoi à tout moment depuis tes paramètres.",
              "Afficher ton historique d'analyses et tes suggestions de recherche.",
              "Limiter les analyses gratuites, pour que le service reste disponible pour tout le monde.",
              "Mesurer la fréquentation du site et repérer les analyses jugées inexactes, afin de les améliorer.",
              "T'informer, si tu as demandé un Pass 24h, du jour où le paiement ouvre, et t'envoyer le lien pour choisir ton mot de passe.",
              "Mesurer combien de personnes souhaitent un Pass 24h, afin de décider s'il faut le construire.",
            ],
          },
          { type: "p", text: "Tes données ne servent <strong>ni à la publicité, ni à du profilage publicitaire</strong>, et ne sont jamais vendues." },
        ],
      },
      {
        title: "3. Ce que ton navigateur garde en mémoire",
        blocks: [
          { type: "p", text: "Le site n'utilise <strong>aucun cookie publicitaire ni traceur tiers</strong>. Il enregistre en revanche quelques informations dans la mémoire locale de ton navigateur. Une seule est transmise au site, celle signalée ci-dessous :" },
          {
            type: "ul",
            items: [
              "ton jeton de connexion, pour te garder connecté ;",
              "ta langue et ton thème d'affichage ;",
              "ton historique de pseudos recherchés, et ceux que tu en as retirés ;",
              "un code de parrainage si tu es arrivé par un lien d'invitation ;",
              "des marqueurs indiquant que tu as déjà donné ton avis, pour ne pas te le redemander ;",
              "<strong>transmis au site :</strong> un identifiant aléatoire tiré par ton navigateur, envoyé à chaque analyse pour décompter tes analyses gratuites du jour. Il ne contient aucune information sur toi et n'est conservé chez nous que sous forme d'empreinte hachée.",
            ],
          },
          { type: "p", text: "Vider les données du site dans ton navigateur les efface toutes." },
        ],
      },
      {
        title: "4. Données de jeu — API Riot Games",
        blocks: [
          { type: "p", text: "Les statistiques de jeu proviennent de l'API publique de Riot Games. Elles sont accessibles à quiconque connaît ton pseudo, indépendamment de notre site." },
          { type: "p", text: "Nous ne conservons pas le détail brut de tes parties. Sont enregistrés uniquement les résultats agrégés d'un rapport — pseudo, score, badge, rang, niveau, moyennes et performance par champion — lorsque tu es connecté (dans ton historique), lorsque tu génères un lien de partage, ou lorsque tu apparais dans le classement." },
        ],
      },
      {
        title: "5. Prestataires techniques",
        blocks: [
          { type: "p", text: "Nous ne vendons ni ne partageons tes données. Elles transitent uniquement par les services nécessaires au fonctionnement du site :" },
          {
            type: "ul",
            items: [
              "<strong>Vercel</strong> (États-Unis) — hébergement du site.",
              "<strong>Hugging Face</strong> (États-Unis) — hébergement du serveur d'analyse.",
              "<strong>Supabase</strong> — base de données.",
              "<strong>Resend</strong> (États-Unis) — envoi et réception des emails.",
              "<strong>Groq</strong> (États-Unis) — rédaction des roasts et des conseils du Coach IA. Seules tes statistiques de jeu agrégées lui sont transmises : ni ton email, ni ton mot de passe, ni ton adresse IP.",
              "<strong>Stripe</strong> — paiement, uniquement si tu achètes un Pass 24h.",
            ],
          },
          { type: "p", text: "Plusieurs de ces prestataires sont établis aux États-Unis. Ces transferts hors Union européenne sont encadrés par les clauses contractuelles types de la Commission européenne." },
        ],
      },
      {
        title: "6. Rapports publics et partage",
        blocks: [
          { type: "p", text: "Trois fonctionnalités rendent un rapport visible par d'autres personnes :" },
          {
            type: "ul",
            items: [
              "<strong>Lien de partage :</strong> tu génères une page publique contenant le pseudo analysé, le score, le badge et le roast. Toute personne disposant du lien peut la consulter, et ces pages sont référençables par les moteurs de recherche.",
              "<strong>Classement :</strong> les pseudos analysés peuvent y apparaître avec leur meilleur score.",
              "<strong>Communauté :</strong> ton profil n'y figure que si tu l'as explicitement rendu public.",
            ],
          },
          { type: "p", text: `Si un rapport te concerne et que tu veux son retrait, écris à <strong>${CONTACT}</strong> : nous le supprimons, et le pseudo est retiré du classement.` },
        ],
      },
      {
        title: "7. Combien de temps nous les gardons",
        blocks: [
          {
            type: "ul",
            items: [
              "<strong>Compte et historique :</strong> tant que ton compte existe. Tu peux le supprimer à tout moment.",
              "<strong>Compteurs des visiteurs sans compte :</strong> 30 jours après la dernière analyse.",
              "<strong>Mesure d'audience :</strong> 13 mois, puis suppression.",
              "<strong>Avis et signalements :</strong> l'empreinte de l'adresse IP est effacée au bout de 12 mois ; la note et le commentaire sont conservés sans identifiant.",
              "<strong>Messages envoyés au support :</strong> 24 mois.",
              "<strong>Rapports partagés :</strong> jusqu'à leur suppression, sur ta demande ou sur décision de notre part.",
              "<strong>Traces de clic sur le Pass 24h :</strong> 24 mois, ou jusqu'à ta demande de suppression.",
            ],
          },
        ],
      },
      {
        title: "8. Sur quelle base légale",
        blocks: [
          {
            type: "ul",
            items: [
              "<strong>Exécution du contrat</strong> — pour ton compte, tes analyses et ton historique : sans ces données, le service ne peut pas fonctionner.",
              "<strong>Intérêt légitime</strong> — pour la limitation des analyses gratuites, la mesure d'audience et la prévention des abus. Ces traitements sont limités au strict nécessaire et tu peux t'y opposer.",
              "<strong>Consentement</strong> — pour la demande de Pass 24h et les emails d'information, que tu peux retirer à tout moment.",
            ],
          },
        ],
      },
      {
        title: "9. Tes droits",
        blocks: [
          { type: "p", text: "Le Règlement général sur la protection des données te donne les droits suivants :" },
          {
            type: "ul",
            items: [
              "<strong>Accès :</strong> savoir quelles données nous détenons sur toi et en obtenir une copie.",
              "<strong>Rectification :</strong> corriger une information inexacte.",
              "<strong>Effacement :</strong> supprimer ton compte et les données associées, depuis Paramètres → Compte → Supprimer mon compte, ou en nous écrivant.",
              "<strong>Portabilité :</strong> récupérer tes données dans un format lisible par une machine.",
              "<strong>Opposition :</strong> t'opposer à un traitement fondé sur notre intérêt légitime.",
              "<strong>Limitation :</strong> demander le gel d'un traitement le temps d'une contestation.",
            ],
          },
          { type: "p", text: `Pour exercer l'un de ces droits, écris à <strong>${CONTACT}</strong>. Nous répondons sous un mois. Si la réponse ne te satisfait pas, tu peux saisir la CNIL (cnil.fr).` },
        ],
      },
      {
        title: "10. Mineurs",
        blocks: [
          { type: "p", text: "Tu peux utiliser Roast My Stats <strong>dès 13 ans</strong>. Entre 13 et 15 ans, l'accord d'un parent ou d'un tuteur légal est nécessaire pour créer un compte ; à partir de 15 ans, tu peux le créer seul. Ce seuil découle de l'article 8 du RGPD." },
        ],
      },
      {
        title: "11. Nous contacter",
        blocks: [
          { type: "p", text: `Pour toute question sur tes données personnelles : <strong>${CONTACT}</strong>.` },
        ],
      },
    ],

    cgu: [
      {
        title: "1. Ce qu'est le service",
        blocks: [
          { type: "p", text: "Roast My Stats analyse des statistiques publiques de League of Legends : le service affiche un score, un profil de jeu, une performance par champion et des conseils de progression." },
          { type: "p", text: "Le service a une <strong>vocation humoristique</strong>. Les commentaires (« roasts ») sont des vannes rédigées automatiquement par une intelligence artificielle à partir de tes statistiques. Ils sont générés par une machine, ne reflètent aucun jugement de notre part et ne disent rien de tes qualités personnelles. Tu choisis toi-même le niveau de moquerie et tu peux en régénérer un à tout moment." },
          { type: "p", text: "Le score est une <strong>estimation</strong> construite à partir de ton rang et de tes dernières parties. Ce n'est pas une mesure officielle et il n'engage en rien Riot Games." },
        ],
      },
      {
        title: "2. Acceptation",
        blocks: [
          { type: "p", text: "Utiliser Roast My Stats vaut acceptation des présentes conditions. Si tu ne les acceptes pas, n'utilise pas le service." },
        ],
      },
      {
        title: "3. Âge minimum",
        blocks: [
          { type: "p", text: "Tu peux utiliser Roast My Stats <strong>dès 13 ans</strong>. Entre 13 et 15 ans, l'accord d'un parent ou d'un tuteur légal est nécessaire pour créer un compte ; à partir de 15 ans, tu peux le créer seul." },
        ],
      },
      {
        title: "4. Ce que tu t'engages à faire",
        blocks: [
          {
            type: "ul",
            items: [
              "Ne pas automatiser de recherches en masse.",
              "Ne pas contourner les limites d'analyses gratuites.",
              "Ne pas utiliser le service à des fins illégales.",
              "Donner des informations exactes à la création de ton compte.",
              "Ne pas publier le rapport d'un autre joueur dans le but de le harceler, de l'humilier ou de lui nuire.",
            ],
          },
          { type: "p", text: `Analyser le pseudo d'un autre joueur est autorisé : ces statistiques sont publiques sur les serveurs de Riot Games. Publier un rapport pour nuire à quelqu'un ne l'est pas. Un roast te semble déplacé, ou un rapport te concerne et tu veux son retrait ? Écris à <strong>${CONTACT}</strong> : nous le supprimons et nous corrigeons le générateur si nécessaire.` },
        ],
      },
      {
        title: "5. Compte gratuit et limites",
        blocks: [
          { type: "p", text: "L'analyse et le roast sont gratuits et ne demandent pas de compte. Le nombre d'analyses par jour est limité, et cette limite est plus élevée avec un compte. Certains réglages, comme le niveau de moquerie, demandent un compte." },
        ],
      },
      {
        title: "6. Le Pass 24h",
        blocks: [
          { type: "p", text: "L'offre payante annoncée est un <strong>Pass 24h à 1,99 €</strong> : un accès unique de vingt-quatre heures à l'analyse approfondie, au Coach IA et aux fonctionnalités réservées, sans abonnement ni reconduction. <strong>Le paiement n'est pas encore ouvert :</strong> aucune somme n'est encaissée aujourd'hui, et cliquer sur le bouton du Pass ne t'engage à rien. Le premier Pass 24h est offert à la création de ton compte, et il n'en est offert <strong>qu'un seul par compte</strong> : en attendant l'ouverture du paiement, un second ne peut pas être obtenu. Il n'existe ni abonnement, ni période d'essai, ni reconduction. Les tarifs et les modalités définitifs seront publiés ici avant tout encaissement." },
          { type: "p", text: "<strong>Droit de rétractation.</strong> Tu disposes de quatorze jours à compter de l'achat d'un Pass pour te rétracter et obtenir le remboursement intégral, sans avoir à te justifier. Un Pass étant un accès qui démarre immédiatement, si tu demandes à en profiter tout de suite et que tu te rétractes ensuite, le remboursement est calculé au prorata des heures que tu n'as pas utilisées. Pour exercer ce droit, il suffit de nous écrire à " + CONTACT + "." },
          { type: "p", text: "Passé ce délai, le Pass déjà consommé n'est pas remboursé. Il n'y a rien à annuler : un Pass ne se reconduit pas et aucun prélèvement ne suit son achat." },
        ],
      },
      {
        title: "7. Données de jeu",
        blocks: [
          { type: "p", text: "Les statistiques proviennent de l'API publique de Riot Games. Roast My Stats n'est responsable ni de leur exactitude, ni de leur disponibilité. Une analyse peut échouer ou être incomplète si l'API de Riot est indisponible." },
        ],
      },
      {
        title: "8. Limitation de responsabilité",
        blocks: [
          { type: "p", text: "Le service est fourni « tel quel », sans garantie de disponibilité continue. Nous ne sommes pas responsables des dommages résultant de son utilisation ou de l'impossibilité de l'utiliser. Ces limites ne s'appliquent pas en cas de faute lourde de notre part, ni aux droits que la loi te garantit en tant que consommateur." },
        ],
      },
      {
        title: "9. Propriété intellectuelle — Riot Games",
        blocks: [
          { type: "p", text: "Roast My Stats isn't endorsed by Riot Games and doesn't reflect the views or opinions of Riot Games or anyone officially involved in producing or managing League of Legends. League of Legends and Riot Games are trademarks or registered trademarks of Riot Games, Inc." },
        ],
      },
      {
        title: "10. Modification des conditions",
        blocks: [
          { type: "p", text: "Ces conditions peuvent être modifiées. La date de dernière mise à jour figure en haut de cette page. En cas de changement affectant un Pass en cours, tu en seras informé par email avant son entrée en vigueur." },
        ],
      },
      {
        title: "11. Droit applicable",
        blocks: [
          { type: "p", text: "Ces conditions sont soumises au droit français. En cas de litige, une solution amiable sera recherchée avant toute action ; à défaut, les tribunaux compétents seront saisis. En tant que consommateur, tu conserves le droit de saisir un médiateur de la consommation." },
        ],
      },
    ],

    mentions: [
      {
        title: "Éditeur du site",
        blocks: [
          { type: "p", text: "Roast My Stats" },
          { type: "p", text: "<em>[Nom ou raison sociale de l'éditeur — à compléter]</em>" },
          { type: "p", text: "<em>[Adresse — à compléter]</em>" },
          { type: "p", text: `Contact : <strong>${CONTACT}</strong>` },
        ],
      },
      {
        title: "Directeur de la publication",
        blocks: [{ type: "p", text: "<em>[À compléter]</em>" }],
      },
      {
        title: "Hébergement",
        blocks: [
          { type: "p", text: "<strong>Site :</strong> Vercel Inc., 340 S Lemon Ave #4133, Walnut, CA 91789, États-Unis — vercel.com" },
          { type: "p", text: "<strong>Serveur d'analyse :</strong> Hugging Face, Inc., 20 Jay Street, Brooklyn, NY 11201, États-Unis — huggingface.co" },
          { type: "p", text: "<strong>Base de données :</strong> Supabase — supabase.com" },
        ],
      },
      {
        title: "Propriété intellectuelle",
        blocks: [
          { type: "p", text: "Roast My Stats isn't endorsed by Riot Games and doesn't reflect the views or opinions of Riot Games or anyone officially involved in producing or managing League of Legends. League of Legends and Riot Games are trademarks or registered trademarks of Riot Games, Inc." },
        ],
      },
      {
        title: "Signalement",
        blocks: [
          { type: "p", text: `Pour signaler un contenu ou demander le retrait d'un rapport : <strong>${CONTACT}</strong>.` },
        ],
      },
    ],
  },

  en: {
    updated: (v) => `Last updated: ${v}`,
    privacy: [
      {
        title: "1. Data we collect",
        blocks: [
          { type: "p", text: "Depending on whether you have an account, Roast My Stats stores:" },
          {
            type: "ul",
            items: [
              "<strong>Account:</strong> email address, first name (optional), password hashed with bcrypt, creation date, referral code and, where applicable, the referral code used at sign-up.",
              "<strong>Analyses:</strong> the summoner names you searched, the scores and badges obtained, and the dates. If you make your profile public, the latest full report is kept to populate the Community page.",
              "<strong>Visitors without an account:</strong> a fingerprint of a random identifier drawn by your browser, plus a fingerprint of your IP address (both SHA-256 hashed with a secret salt, not reversible in practice), solely to limit the number of free analyses per day. The count is kept against the browser identifier; the IP fingerprint only caps bulk usage. Neither the plain identifier nor the address itself is kept.",
              "<strong>Audience measurement:</strong> a fingerprint of your IP address (SHA-256 hashed, not reversible in practice) and the date, to count unique daily visitors. We never cross-reference it with any other data.",
              "<strong>Ratings and reports:</strong> the rating, any comment, and a hashed fingerprint of the IP address, to stop the same visitor from voting repeatedly.",
              "<strong>24h Pass:</strong> your email address, and a timestamped record of your click on the Pass button — together with the browser fingerprint described above, the language, and your account ID if you have one. Since payment is not open yet, these records serve to measure interest in the offer before building it.",
              "<strong>Messages:</strong> the content of the emails you send us, so we can reply.",
              "<strong>Pass payment:</strong> your customer ID at our payment provider, if you ever buy a 24h Pass. We never see or store your card number.",
            ],
          },
        ],
      },
      {
        title: "2. What we use it for",
        blocks: [
          {
            type: "ul",
            items: [
              "Creating your account, signing you in, and letting you delete it.",
              "Sending verification and password-reset emails.",
              "Sending you a weekly recap of your analysed games, if you played during the week. Every email carries a one-click unsubscribe link, and you can turn it off at any time in your settings.",
              "Showing your analysis history and search suggestions.",
              "Limiting free analyses, so the service stays available to everyone.",
              "Measuring site traffic and spotting analyses flagged as inaccurate, in order to improve them.",
              "Telling you when payment opens, if you asked for a 24h Pass, and sending you the link to pick a password.",
              "Measuring how many people want a 24h Pass, so we can decide whether to build it.",
            ],
          },
          { type: "p", text: "Your data is used <strong>neither for advertising nor for ad profiling</strong>, and is never sold." },
        ],
      },
      {
        title: "3. What your browser stores",
        blocks: [
          { type: "p", text: "The site uses <strong>no advertising cookies and no third-party trackers</strong>. It does store a few items in your browser's local storage. Only one of them is sent to the site, the one flagged below:" },
          {
            type: "ul",
            items: [
              "your session token, to keep you signed in;",
              "your language and display theme;",
              "your history of searched summoner names, and those you removed from it;",
              "a referral code if you arrived through an invitation link;",
              "markers recording that you already gave feedback, so we do not ask again;",
              "<strong>sent to the site:</strong> a random identifier drawn by your browser, sent with every analysis to count your free analyses for the day. It holds no information about you and is only ever kept as a hashed fingerprint on our side.",
            ],
          },
          { type: "p", text: "Clearing the site's data in your browser erases all of them." },
        ],
      },
      {
        title: "4. Game data — Riot Games API",
        blocks: [
          { type: "p", text: "Game statistics come from the public Riot Games API. They are accessible to anyone who knows your summoner name, independently of our site." },
          { type: "p", text: "We do not keep the raw detail of your matches. Only the aggregated results of a report are stored — summoner name, score, badge, rank, level, averages and per-champion performance — when you are signed in (in your history), when you generate a share link, or when you appear on the leaderboard." },
        ],
      },
      {
        title: "5. Technical providers",
        blocks: [
          { type: "p", text: "We neither sell nor share your data. It only passes through the services required to run the site:" },
          {
            type: "ul",
            items: [
              "<strong>Vercel</strong> (United States) — website hosting.",
              "<strong>Hugging Face</strong> (United States) — analysis server hosting.",
              "<strong>Supabase</strong> — database.",
              "<strong>Resend</strong> (United States) — sending and receiving emails.",
              "<strong>Groq</strong> (United States) — writing the roasts and the AI Coach advice. Only your aggregated game statistics are sent: never your email, your password or your IP address.",
              "<strong>Stripe</strong> — payment, only if you buy a 24h Pass.",
            ],
          },
          { type: "p", text: "Several of these providers are established in the United States. Those transfers outside the European Union are covered by the European Commission's standard contractual clauses." },
        ],
      },
      {
        title: "6. Public reports and sharing",
        blocks: [
          { type: "p", text: "Three features make a report visible to other people:" },
          {
            type: "ul",
            items: [
              "<strong>Share link:</strong> you generate a public page containing the summoner name analysed, the score, the badge and the roast. Anyone with the link can view it, and those pages can be indexed by search engines.",
              "<strong>Leaderboard:</strong> analysed summoner names may appear there with their best score.",
              "<strong>Community:</strong> your profile only appears there if you explicitly made it public.",
            ],
          },
          { type: "p", text: `If a report concerns you and you want it taken down, write to <strong>${CONTACT}</strong>: we delete it, and the name is removed from the leaderboard.` },
        ],
      },
      {
        title: "7. How long we keep it",
        blocks: [
          {
            type: "ul",
            items: [
              "<strong>Account and history:</strong> as long as your account exists. You can delete it at any time.",
              "<strong>Counters for visitors without an account:</strong> 30 days after the last analysis.",
              "<strong>Audience measurement:</strong> 13 months, then deleted.",
              "<strong>Ratings and reports:</strong> the IP address fingerprint is erased after 12 months; the rating and comment are kept without any identifier.",
              "<strong>Messages sent to support:</strong> 24 months.",
              "<strong>Shared reports:</strong> until deleted, at your request or at our discretion.",
              "<strong>24h Pass click records:</strong> 24 months, or until you ask for deletion.",
            ],
          },
        ],
      },
      {
        title: "8. Legal basis",
        blocks: [
          {
            type: "ul",
            items: [
              "<strong>Performance of a contract</strong> — for your account, your analyses and your history: without this data the service cannot work.",
              "<strong>Legitimate interest</strong> — for limiting free analyses, measuring audience and preventing abuse. These are kept to the strict minimum and you may object to them.",
              "<strong>Consent</strong> — for requesting a 24h Pass and receiving announcement emails, which you can withdraw at any time.",
            ],
          },
        ],
      },
      {
        title: "9. Your rights",
        blocks: [
          { type: "p", text: "The General Data Protection Regulation grants you the following rights:" },
          {
            type: "ul",
            items: [
              "<strong>Access:</strong> find out what data we hold about you and obtain a copy.",
              "<strong>Rectification:</strong> correct inaccurate information.",
              "<strong>Erasure:</strong> delete your account and the associated data, from Settings → Account → Delete my account, or by writing to us.",
              "<strong>Portability:</strong> receive your data in a machine-readable format.",
              "<strong>Objection:</strong> object to processing based on our legitimate interest.",
              "<strong>Restriction:</strong> ask us to freeze processing while a claim is being examined.",
            ],
          },
          { type: "p", text: `To exercise any of these rights, write to <strong>${CONTACT}</strong>. We answer within one month. If our answer does not satisfy you, you may lodge a complaint with the CNIL (cnil.fr) or with your national data protection authority.` },
        ],
      },
      {
        title: "10. Minors",
        blocks: [
          { type: "p", text: "You may use Roast My Stats <strong>from the age of 13</strong>. Between 13 and 15, a parent's or legal guardian's consent is required to create an account; from 15 you may create one on your own. This threshold comes from Article 8 of the GDPR, as applied in France." },
        ],
      },
      {
        title: "11. Contact",
        blocks: [
          { type: "p", text: `For any question about your personal data: <strong>${CONTACT}</strong>.` },
        ],
      },
    ],

    cgu: [
      {
        title: "1. What the service is",
        blocks: [
          { type: "p", text: "Roast My Stats analyses public League of Legends statistics: it shows a score, a play profile, per-champion performance and improvement tips." },
          { type: "p", text: "The service is <strong>meant to be humorous</strong>. The comments (\"roasts\") are jokes written automatically by an artificial intelligence from your statistics. They are machine-generated, reflect no judgement on our part, and say nothing about you as a person. You choose the level of mockery yourself and can regenerate a roast at any time." },
          { type: "p", text: "The score is an <strong>estimate</strong> built from your rank and your recent games. It is not an official measurement and in no way commits Riot Games." },
        ],
      },
      {
        title: "2. Acceptance",
        blocks: [
          { type: "p", text: "Using Roast My Stats means accepting these terms. If you do not accept them, do not use the service." },
        ],
      },
      {
        title: "3. Minimum age",
        blocks: [
          { type: "p", text: "You may use Roast My Stats <strong>from the age of 13</strong>. Between 13 and 15, a parent's or legal guardian's consent is required to create an account; from 15 you may create one on your own." },
        ],
      },
      {
        title: "4. What you agree to",
        blocks: [
          {
            type: "ul",
            items: [
              "Not to automate bulk searches.",
              "Not to circumvent the free analysis limits.",
              "Not to use the service for unlawful purposes.",
              "To provide accurate information when creating your account.",
              "Not to publish another player's report in order to harass, humiliate or harm them.",
            ],
          },
          { type: "p", text: `Analysing another player's summoner name is allowed: those statistics are public on Riot Games' servers. Publishing a report to harm someone is not. If a roast strikes you as out of line, or a report concerns you and you want it removed, write to <strong>${CONTACT}</strong>: we delete it and fix the generator if needed.` },
        ],
      },
      {
        title: "5. Free account and limits",
        blocks: [
          { type: "p", text: "The analysis and the roast are free and require no account. The number of analyses per day is limited, and that limit is higher with an account. Some settings, such as the mockery level, require an account." },
        ],
      },
      {
        title: "6. The 24h Pass",
        blocks: [
          { type: "p", text: "The announced paid offer is a <strong>24h Pass at €1.99</strong>: a one-off twenty-four-hour access to the deeper analysis, the AI Coach and reserved features, with no subscription and no renewal. <strong>Payment is not open yet:</strong> nothing is charged today, and clicking the Pass button commits you to nothing. The first 24h Pass is free when you create your account, and <strong>only one is given per account</strong>: until payment opens, a second one cannot be obtained. There is no subscription, no trial period and no renewal. Final pricing and terms will be published here before any charge is made." },
          { type: "p", text: "<strong>Right of withdrawal.</strong> You have fourteen days from buying a Pass to withdraw and obtain a full refund, without having to give a reason. A Pass is an access that starts immediately, so if you ask to use it straight away and then withdraw, the refund is calculated pro rata for the hours you have not used. To exercise this right, simply write to " + CONTACT + "." },
          { type: "p", text: "After that period a Pass already used is not refunded. There is nothing to cancel: a Pass does not renew and no charge follows its purchase." },
        ],
      },
      {
        title: "7. Game data",
        blocks: [
          { type: "p", text: "Statistics come from the public Riot Games API. Roast My Stats is responsible neither for their accuracy nor for their availability. An analysis may fail or be incomplete if Riot's API is unavailable." },
        ],
      },
      {
        title: "8. Limitation of liability",
        blocks: [
          { type: "p", text: "The service is provided \"as is\", with no guarantee of continuous availability. We are not liable for damages resulting from its use or from being unable to use it. These limits do not apply in the event of gross negligence on our part, nor to the rights the law guarantees you as a consumer." },
        ],
      },
      {
        title: "9. Intellectual property — Riot Games",
        blocks: [
          { type: "p", text: "Roast My Stats isn't endorsed by Riot Games and doesn't reflect the views or opinions of Riot Games or anyone officially involved in producing or managing League of Legends. League of Legends and Riot Games are trademarks or registered trademarks of Riot Games, Inc." },
        ],
      },
      {
        title: "10. Changes to these terms",
        blocks: [
          { type: "p", text: "These terms may change. The date of the last update appears at the top of this page. If a change affects an active Pass, you will be told by email before it takes effect." },
        ],
      },
      {
        title: "11. Governing law",
        blocks: [
          { type: "p", text: "These terms are governed by French law. In the event of a dispute, an amicable solution will be sought before any action; failing that, the competent courts will be seized. As a consumer, you keep the right to refer the matter to a consumer mediator." },
        ],
      },
    ],

    mentions: [
      {
        title: "Site publisher",
        blocks: [
          { type: "p", text: "Roast My Stats" },
          { type: "p", text: "<em>[Publisher name or company name — to be completed]</em>" },
          { type: "p", text: "<em>[Address — to be completed]</em>" },
          { type: "p", text: `Contact: <strong>${CONTACT}</strong>` },
        ],
      },
      {
        title: "Publication director",
        blocks: [{ type: "p", text: "<em>[To be completed]</em>" }],
      },
      {
        title: "Hosting",
        blocks: [
          { type: "p", text: "<strong>Website:</strong> Vercel Inc., 340 S Lemon Ave #4133, Walnut, CA 91789, United States — vercel.com" },
          { type: "p", text: "<strong>Analysis server:</strong> Hugging Face, Inc., 20 Jay Street, Brooklyn, NY 11201, United States — huggingface.co" },
          { type: "p", text: "<strong>Database:</strong> Supabase — supabase.com" },
        ],
      },
      {
        title: "Intellectual property",
        blocks: [
          { type: "p", text: "Roast My Stats isn't endorsed by Riot Games and doesn't reflect the views or opinions of Riot Games or anyone officially involved in producing or managing League of Legends. League of Legends and Riot Games are trademarks or registered trademarks of Riot Games, Inc." },
        ],
      },
      {
        title: "Reporting",
        blocks: [
          { type: "p", text: `To report content or request the removal of a report: <strong>${CONTACT}</strong>.` },
        ],
      },
    ],
  },
};

export default legal;
